/**
 * @file components/settings/LaboratoryFormDrawer.tsx
 * @description 實驗室新增 / 編輯 Drawer。受控；由父層管理 open 與當前編輯記錄。
 */
import { useEffect } from 'react';
import { Button, Drawer, Form, Input, InputNumber, Space, Switch } from 'antd';
import { message } from '@/lib/antd-app';
import type { CreateLaboratoryInput, LaboratoryDto } from '@zhcb/shared';
import { useCreateLaboratory, useUpdateLaboratory } from '@/hooks/useLaboratories';

interface FormValues {
  name: string;
  accreditationNo?: string | null;
  phone?: string | null;
  address?: string | null;
  contactPerson?: string | null;
  remark?: string | null;
  /** 實驗室證書有效期（序119：到期前 30 天提醒管理組；YYYY-MM-DD） */
  certValidUntil?: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface Props {
  open: boolean;
  /** 編輯模式時傳入；新增時為 null */
  editing: LaboratoryDto | null;
  onClose: () => void;
}

export function LaboratoryFormDrawer({ open, editing, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const create = useCreateLaboratory();
  const update = useUpdateLaboratory();

  // open 切換 / editing 變更 時重置表單值
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        accreditationNo: editing.accreditationNo ?? undefined,
        phone: editing.phone ?? undefined,
        address: editing.address ?? undefined,
        contactPerson: editing.contactPerson ?? undefined,
        remark: editing.remark ?? undefined,
        certValidUntil: editing.certValidUntil ?? undefined,
        isActive: editing.isActive,
        sortOrder: editing.sortOrder,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0 });
    }
  }, [open, editing, form]);

  const submit = async (values: FormValues) => {
    const payload: CreateLaboratoryInput = {
      name: values.name,
      accreditationNo: values.accreditationNo || null,
      phone: values.phone || null,
      address: values.address || null,
      contactPerson: values.contactPerson || null,
      remark: values.remark || null,
      certValidUntil: values.certValidUntil || null,
      isActive: values.isActive,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: payload });
        message.success('已更新');
      } else {
        await create.mutateAsync(payload);
        message.success('已新增');
      }
      onClose();
    } catch (e) {
      message.error((e as Error).message || '保存失敗');
    }
  };

  return (
    <Drawer
      title={editing ? `編輯實驗室 #${editing.id}` : '新增實驗室'}
      open={open}
      onClose={onClose}
      size={520}
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={create.isPending || update.isPending}
            onClick={() => form.submit()}
          >
            {editing ? '儲存' : '提交'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="name" label="名稱" rules={[{ required: true, max: 200 }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="accreditationNo" label="認證號（如 TAF-1234）">
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="contactPerson" label="聯絡人">
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="phone" label="電話">
          <Input maxLength={50} />
        </Form.Item>
        <Form.Item name="address" label="地址">
          <Input maxLength={500} />
        </Form.Item>
        <Form.Item name="remark" label="備註">
          <Input.TextArea rows={3} maxLength={5000} showCount />
        </Form.Item>
        {/* 序119：實驗室證書有效期——到期前 30 天自動提醒管理組 */}
        <Form.Item
          name="certValidUntil"
          label="實驗室證書有效期"
          tooltip="格式 YYYY-MM-DD；到期前 30 天系統自動提醒管理組"
          rules={[{ pattern: /^\d{4}-\d{2}-\d{2}$/, message: '格式須為 YYYY-MM-DD' }]}
        >
          <Input placeholder="YYYY-MM-DD" maxLength={10} />
        </Form.Item>
        <Space>
          <Form.Item name="isActive" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序權重" tooltip="值越小越前">
            <InputNumber min={0} />
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
}
